import React, { MouseEvent, ReactNode, useState } from 'react';

import { Avatar } from '@mui/material';
import clsx from 'clsx';
import Highlighter from 'react-highlight-words';

import { NoImage } from 'assets/images';
import { customStyles, LIGHT_ORANGE } from 'components/SelectCreatable/customStyles';
import ClearIndicator from 'components/SelectIndicators/ClearIndicator';
import DropdownIndicator from 'components/SelectIndicators/DropdownIndicator';
import useMobileCheck from 'hooks/useMobileCheck';
import useTabletCheck from 'hooks/useTabletCheck';
import { formatOptionLabel, MetaType, OptionType } from 'utils/inputSelect';

import styles from './SelectWrapper.module.scss';

const SELECTOR_CONTAINER_CLASS_REGEX = new RegExp(/ValueContainer/g);
const LOGO_SIZE_PX = 24;

interface SelectWrapperProps extends React.PropsWithChildren<{}> {
  label?: string;
  inputContainerClassname?: string;
  errorMessage?: string;
  caption?: string;
  loading?: boolean | null;
  isDisabled?: boolean;
  showSearchIndicator?: boolean;
  value?: OptionType;
  isOptionWithIcon?: boolean;
}

const formatOptionLabelWithIcon = (option: OptionType, labelMeta: MetaType) => {
  if (option.__isNew__) {
    return option.label;
  }

  return (
    <div className={styles.option}>
      {option.iconUrl ? (
        <Avatar src={option.iconUrl} sx={{ width: LOGO_SIZE_PX, height: LOGO_SIZE_PX }} />
      ) : (
        option.label && <Avatar sx={{ width: LOGO_SIZE_PX, height: LOGO_SIZE_PX }} children={<NoImage />} />
      )}
      <Highlighter
        searchWords={[labelMeta.inputValue]}
        textToHighlight={option.label}
        autoEscape
        highlightStyle={{ backgroundColor: LIGHT_ORANGE }}
      />
    </div>
  );
};

const isClickOnSelect = (event: MouseEvent<HTMLDivElement>) => {
  return (event.target as HTMLElement).classList.value.search(SELECTOR_CONTAINER_CLASS_REGEX) >= 0;
};

const SelectWrapper = ({
  label,
  inputContainerClassname,
  children,
  errorMessage,
  caption,
  loading,
  isDisabled,
  showSearchIndicator,
  value,
  isOptionWithIcon = false,
}: SelectWrapperProps) => {
  const [isHover, setIsHover] = useState<boolean>(false);
  const isTablet = useTabletCheck();
  const isMobile = useMobileCheck();

  const addProperty = (node: ReactNode, showClearIndicator: boolean) => {
    const childrenWithProps = React.Children.map(node, (child) => {
      if (React.isValidElement(child)) {
        const components = { ClearIndicator };

        return React.cloneElement(child, {
          // @ts-ignore
          showClearIndicator,
          blurInputOnSelect: true,
          formatOptionLabel: isOptionWithIcon ? formatOptionLabelWithIcon : formatOptionLabel,
          noOptionsMessage: () => null,
          components: showSearchIndicator ? Object.assign(components, { DropdownIndicator }) : components,
          styles: customStyles({
            loading: !!loading,
            error: errorMessage || '',
            disabled: isDisabled,
            isTablet,
            isMobile,
            isSelected: !!value?.label,
          }),
        });
      }

      return child;
    });

    return <>{childrenWithProps}</>;
  };

  const handleMouseEnter = () => setIsHover(true);
  const handleMouseLeave = () => setIsHover(false);

  return (
    <div className={clsx(styles.inputContainer, inputContainerClassname)}>
      {label && <label className={clsx(styles.label, { [styles.no_interaction_text]: loading })}>{label}</label>}
      <div
        className={styles.wrapper}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(event) => (isClickOnSelect(event) ? handleMouseEnter() : handleMouseLeave())}
      >
        {addProperty(children, isHover)}
      </div>
      {(errorMessage || caption) && <p className={clsx(styles.errorMessage)}>{errorMessage}</p>}
    </div>
  );
};

export default SelectWrapper;
